/** Set Deadline / Change Deadline — port of the original extendModal(). Every change needs a reason and is recorded permanently. */
import { useState } from 'react';
import type { DeadlineChangeInput, Project } from '@/types/models';
import { fmtDate, today } from '@/domain/format';
import { deadline } from '@/domain/projects';
import { LIMITS, validateDeadlineChange, type FieldErrors } from '@/domain/validation';
import { useAction, useDataset } from '@/hooks/data';
import { ErrorState, Field, LoadingState, ModalFrame } from '@/ui/kit';

export function DeadlineModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const ds = useDataset();
  const p = ds.data?.projects.find((x) => x.id === projectId);
  const title = p ? `${deadline(p) ? 'Change Deadline' : 'Set Deadline'} — ${p.name}` : 'Change Deadline';
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  if (!p) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  return <DeadlineForm key={p.id} project={p} title={title} onClose={onClose} />;
}

function DeadlineForm({ project: p, title, onClose }: { project: Project; title: string; onClose: () => void }) {
  const cur = deadline(p);
  const [f, setF] = useState<DeadlineChangeInput>({ to: '', reason: '' });
  const [err, setErr] = useState<FieldErrors>({});
  const save = useAction((s, input: DeadlineChangeInput) => s.projects.changeDeadline(p.id, input), cur ? 'Deadline changed successfully.' : 'Deadline set successfully.');
  const submit = async () => {
    const e = validateDeadlineChange(f, cur);
    setErr(e);
    if (Object.keys(e).length) return;
    const ok = await save.run({ to: f.to, reason: f.reason.trim() });
    if (ok) onClose();
  };
  const extensions = p.extensions || [];
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={save.pending}>
              {cur ? 'Change Deadline' : 'Set Deadline'}
            </button>
          </>
        }
      >
        <div className="grid2">
          <Field label="Current deadline">
            <input type="text" value={fmtDate(cur)} disabled />
          </Field>
          <Field label={cur ? 'New deadline' : 'Deadline'} required error={err.to}>
            <input type="date" name="to" value={f.to} min={cur || today()} onChange={(e) => setF({ ...f, to: e.target.value })} />
          </Field>
        </div>
        <Field label={cur ? 'Reason for change' : 'Reason / basis for this deadline'} required error={err.reason}>
          <textarea
            name="reason"
            maxLength={LIMITS.reason}
            placeholder={
              cur
                ? 'Why is the deadline moving? e.g. Two developers on leave until 10 Oct; client added 3 new modules. Recorded permanently.'
                : 'e.g. Agreed with client on 28 Sep; scope is 4 modules.'
            }
            value={f.reason}
            onChange={(e) => setF({ ...f, reason: e.target.value })}
          />
        </Field>
        {extensions.length ? (
          <>
            <label>Previous changes</label>
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>On</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {extensions.map((e, i) => (
                    <tr key={i}>
                      <td className="mono">{fmtDate(e.on)}</td>
                      <td className="mono">{fmtDate(e.from)}</td>
                      <td className="mono">{fmtDate(e.to)}</td>
                      <td>{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </ModalFrame>
    </form>
  );
}
